// @ts-nocheck
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import transformContentHtml from "@insite/client-framework/Common/Utilities/transformContentHtml";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Accordion, { AccordionProps } from "@insite/mobius/Accordion";
import { AccordionSectionPresentationProps, ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconMemo, IconProps } from "@insite/mobius/Icon";
import File from "@insite/mobius/Icons/File";
import Link, { LinkPresentationProps, StyledTypography } from "@insite/mobius/Link"; // Sas
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import * as React from "react";
import { css } from "styled-components";

// Sas --->
import useParentProductState from "@store/Framework/Store/Foundation/Data/Products/ProductsHooks/useParentProductState";
import getColor from "@insite/mobius/utilities/getColor";
import { productDetailsAccordionWrapperId } from "@store/Library/Widgets/ProductDetails/ProductDetailsAccordion";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
// <--- Sas

const enum fields {
    showDocuments = "showDocuments",
    showAttributes = "showAttributes",
    attributesPosition = "attributesPosition",
    documentsPosition = "documentsPosition",
}

type Props = WidgetProps & HasProduct;

export interface ProductDetailsSpecificationsStyles {
    specificationsAccordion?: AccordionProps;
    specificationsAccordionSection?: AccordionSectionPresentationProps;
    documentsContainer?: GridContainerProps;
    documentGridItem?: GridItemProps;
    documentLink?: LinkPresentationProps;
    documentIcon?: IconProps;
    documentNameText?: TypographyPresentationProps;
    attributeTypeWrapper?: InjectableCss;
    attributeTypeLabelText?: TypographyPresentationProps;
    attributeTypeValuesText?: TypographyPresentationProps;
    // Sas
    wrapper?: InjectableCss;
    specificationWrapper?: InjectableCss;
    specificationTitle?: TypographyPresentationProps;
    specificationLink?: LinkPresentationProps;
}

export const specificationsStyles: ProductDetailsSpecificationsStyles = {
    specificationsAccordion: {
        headingLevel: 2,
    },
    documentGridItem: {
        width: [12, 12, 6, 6, 6],
    },
    documentIcon: {
        src: File,
        color: "text.link",
        css: css`
            margin-right: ${pxToRem(10)};
        `,
    },
    documentNameText: {
        css: css`
            vertical-align: super;
        `,
    },
    attributeTypeLabelText: {
        weight: "bold",
    },
    specificationTitle: {
        color: "primary",
        css: css`
            margin-bottom: 0.75em;
            ${({ theme }) => `
                font-size: ${theme.design.typography.size.base};
                font-weight: ${theme.design.typography.weight.medium};
            `}
        `,
    },
    specificationWrapper: {
        css: css`
            > * {
                padding: 0;
                margin: 0;
                margin-top: 0.75em;
                margin-bottom: 0.5em;
            }

            ul {
                list-style: none;

                & li {
                    line-height: 1.85em;

                    &::before {
                        content: "\\2022";
                        color: ${getColor("primary")};
                        font-weight: bold;
                        display: inline-block;
                        top: 25%;
                        width: 1em;
                        font-size: ${pxToRem(22)};
                        line-height: 0.5em;
                        transform: translateY(25%);
                    }
                }
            }
        `,
    },
    specificationLink: {
        typographyProps: {
            css: css`
                margin-left: 1.5em;
            `,
        },
    },
};

const styles = specificationsStyles;

const ProductDetailsSpecifications: React.FC<Props> = ({
    product: { brand, attributeTypes, documents, specifications },
    fields: { showDocuments, showAttributes, documentsPosition, attributesPosition },
}) => {
    const parentProductState = useParentProductState(); // Sas
    const renderingDocuments = showDocuments && documents && documents.length > 0;
    const renderingAttributes = showAttributes && (brand || (attributeTypes && attributeTypes?.length > 0));
    const renderingSpecifications = specifications && specifications.length > 0;
    const renderingParentSpecifications =
        parentProductState?.specifications && parentProductState?.specifications.length > 0; // Sas
    const documentsFirst = documentsPosition === "first";
    const attributesFirst = attributesPosition === "first";

    if (
        !renderingDocuments &&
        !renderingAttributes &&
        !renderingSpecifications &&
        !renderingParentSpecifications /* Sas */
    ) {
        return null;
    }

    // Sas
    const productSpecifications = renderingSpecifications ? specifications : parentProductState?.specifications;

    const renderViewMoreLink = () => {
        const onClickViewMore = () => {
            const accordionWrapper = document.getElementById(productDetailsAccordionWrapperId);

            if (accordionWrapper) {
                window.scrollTo(0, accordionWrapper.offsetTop);
            }
        };

        return (
            <Link
                onClick={() => {
                    onClickViewMore();
                }}
                {...styles.specificationLink}
            >
                {translate("View More")}
            </Link>
        );
    };
    // Sas

    const documentsAccordion = renderingDocuments && (
        <ManagedAccordionSection
            title={translate("Documents")}
            data-test-selector="productDetails_specifications_documents"
            initialExpanded={
                ((!attributesFirst || !renderingAttributes) && documentsFirst) ||
                (!renderingSpecifications && !renderingAttributes)
            }
            {...styles.specificationsAccordionSection}
        >
            <GridContainer {...styles.documentsContainer}>
                {documents!.map(document => (
                    <GridItem key={document.id.toString()} {...styles.documentGridItem}>
                        <Link
                            href={document.filePath}
                            target="_new"
                            {...styles.documentLink}
                            data-test-selector={`productDetails_${document.id}`}
                        >
                            <IconMemo {...styles.documentIcon} />
                            <Typography {...styles.documentNameText}>{document.name}</Typography>
                        </Link>
                    </GridItem>
                ))}
            </GridContainer>
        </ManagedAccordionSection>
    );

    const attributesAccordion = renderingAttributes && (
        <ManagedAccordionSection
            title={translate("Attributes")}
            initialExpanded={attributesFirst || (!renderingSpecifications && !renderingDocuments)}
            data-test-selector="productDetails_specifications_attributes"
            {...styles.specificationsAccordionSection}
        >
            {brand && (
                <StyledWrapper {...styles.attributeTypeWrapper}>
                    <Typography {...styles.attributeTypeLabelText}>{translate("Brand")}:&nbsp;</Typography>
                    <Typography {...styles.attributeTypeValuesText}>{brand.name}</Typography>
                </StyledWrapper>
            )}
            {attributeTypes &&
                attributeTypes.map(attributeType => (
                    <StyledWrapper {...styles.attributeTypeWrapper} key={attributeType.id.toString()}>
                        <Typography {...styles.attributeTypeLabelText}>
                            {attributeType.label || attributeType.name}:&nbsp;
                        </Typography>
                        <Typography {...styles.attributeTypeValuesText}>
                            {attributeType
                                .attributeValues!.map(attributeValue => attributeValue.valueDisplay)
                                .join(", ")}
                        </Typography>
                    </StyledWrapper>
                ))}
        </ManagedAccordionSection>
    );

    return (
        // Sas
        <StyledWrapper {...styles.wrapper}>
            {attributesFirst && attributesAccordion}
            {documentsFirst && documentsAccordion}
            <Typography {...styles.specificationTitle} data-test-selector="ProductDetailsPageTitle">
                {translate("Features & Benefits")}
            </Typography>
            {productSpecifications?.map((specification, index) => (
                <StyledWrapper {...styles.specificationWrapper} key={`${index}${specification}`}>
                    {parse(specification.htmlContent, parserOptions)}
                </StyledWrapper>
            ))}
            {renderViewMoreLink()}
            {!attributesFirst && attributesAccordion}
            {!documentsFirst && documentsAccordion}
        </StyledWrapper>
    );
    // Sas
};

const widgetModule: WidgetModule = {
    component: withProduct(ProductDetailsSpecifications),
    definition: {
        displayName: "Specification",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
        fieldDefinitions: [
            {
                name: fields.showDocuments,
                displayName: "Show Documents",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.documentsPosition,
                displayName: "Documents Position",
                editorTemplate: "RadioButtonsField",
                defaultValue: "first",
                horizontal: true,
                fieldType: "General",
                sortOrder: 2,
                options: [
                    {
                        displayName: "Display First",
                        value: "first",
                    },
                    {
                        displayName: "Display Last",
                        value: "last",
                    },
                ],
                isVisible: widget => widget.fields.showDocuments,
            },
            {
                name: fields.showAttributes,
                displayName: "Show Attributes",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 3,
            },
            {
                name: fields.attributesPosition,
                displayName: "Attributes Position",
                editorTemplate: "RadioButtonsField",
                defaultValue: "first",
                horizontal: true,
                fieldType: "General",
                sortOrder: 4,
                options: [
                    {
                        displayName: "Display First",
                        value: "first",
                    },
                    {
                        displayName: "Display Last",
                        value: "last",
                    },
                ],
                isVisible: widget => widget.fields.showAttributes,
            },
        ],
    },
};

export default widgetModule;
